// kanjo/Widgets.jsx — Shared UI primitives.
//
// Empty   — empty-state with optional icon, title, body, and up to two CTAs.
// Sheet   — bottom sheet with backdrop; closes on backdrop tap or Esc.
// Toast   — single-slot toast surface driven by window.kanjoToast(...).
// Skeleton, SkeletonText, SkeletonCard — animated loading placeholders.
// Stepper — qty −/+ with tap-to-numpad.
// KPITile / KPIRow — labelled metric tiles.

// ─── Local date helpers ─────────────────────────────────────
// "Today" / "this month" in the DEVICE's timezone, not UTC. Using
// toISOString() directly rolled the date over in the evening for users west of
// UTC (e.g. 5pm PT on May 31 → UTC June 1 → the app showed an empty "June").
// Components call these as globals (Widgets loads before the data screens).
function localToday() { return new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10); }
function localMonth() { return localToday().slice(0, 7); }

// ─── Empty ──────────────────────────────────────────────────
function Empty({ icon, title, body, primaryCta, secondaryCta }) {
  return (
    <div style={{ padding: '40px 28px', textAlign: 'center' }}>
      {icon && (
        <div style={{
          width: 56, height: 56, borderRadius: 28,
          background: 'var(--surface-3)', color: 'var(--ink-3)',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: 18,
        }}>{icon}</div>
      )}
      {title && <div style={{ fontSize: 20, fontWeight: 600, letterSpacing: '-0.015em', marginBottom: 6 }}>{title}</div>}
      {body && <div style={{ fontSize: 14, color: 'var(--on-dim)', maxWidth: 280, margin: '0 auto', lineHeight: 1.5 }}>{body}</div>}
      {(primaryCta || secondaryCta) && (
        <div style={{ marginTop: 22, display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 280, marginLeft: 'auto', marginRight: 'auto' }}>
          {primaryCta && (
            <KLink to={primaryCta.to} asButton style={{ width: '100%' }}>
              <button className="btn primary lg" style={{ width: '100%', cursor: 'pointer' }} onClick={primaryCta.onClick}>
                {primaryCta.label}
              </button>
            </KLink>
          )}
          {secondaryCta && (
            <KLink to={secondaryCta.to} asButton style={{ width: '100%' }}>
              <button className="btn ghost lg" style={{ width: '100%', cursor: 'pointer' }} onClick={secondaryCta.onClick}>
                {secondaryCta.label}
              </button>
            </KLink>
          )}
        </div>
      )}
    </div>
  );
}

// ─── ErrorBanner ────────────────────────────────────────────
// Block-level error message as a readable chip. Uses the OPAQUE --danger-bg
// fill (not the semi-transparent --danger-soft, which only reads on white cards
// — over the dark purple canvas a 12% tint stays dark and drops --danger text to
// ~3.1:1). --danger-bg is tuned per theme so --danger text clears WCAG AA (≥4.5)
// whether the banner sits on a card or the canvas. Pass `style` to tune outer
// spacing (margins). Inline status text inside a white card can keep a plain
// `color: var(--danger)`.
function ErrorBanner({ children, style }) {
  return (
    <div role="alert" style={{
      padding: '12px 14px', borderRadius: 12,
      background: 'var(--danger-bg)', color: 'var(--danger)',
      fontSize: 13, lineHeight: 1.45, ...(style || {}),
    }}>{children}</div>
  );
}

// ─── WarningBanner ──────────────────────────────────────────
// Non-error advisory (amber outline). Same opaque-fill discipline as
// ErrorBanner: uses --warning-bg (opaque) — NOT --warning-soft, whose 18% tint
// stays dark over the canvas and made --ink text dark-on-dark. --warning-bg is
// tuned per theme so the --ink body text clears WCAG AA on card or canvas.
function WarningBanner({ children, style }) {
  return (
    <div role="alert" style={{
      padding: '12px 14px', borderRadius: 12,
      background: 'var(--warning-bg)', border: '1px solid var(--warning)',
      color: 'var(--ink)', fontSize: 13, lineHeight: 1.45, ...(style || {}),
    }}>{children}</div>
  );
}

// ─── Sheet (bottom sheet) ───────────────────────────────────
function Sheet({ open, onClose, title, children, height = 'auto' }) {
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div role="dialog" aria-modal="true" aria-label={title || 'Sheet'}
         style={{
           // fixed (not absolute): anchor to the viewport, not the scrollable
           // content — otherwise the panel lands at the bottom of a tall page and
           // the user has to scroll to reach it.
           position: 'fixed', inset: 0, zIndex: 50,
           display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
         }}>
      <div onClick={onClose} aria-hidden="true"
           style={{
             position: 'absolute', inset: 0,
             background: 'rgba(0,0,0,0.42)',
             animation: 'kanjo-sheet-fade 0.18s ease-out',
           }}/>
      <div style={{
        position: 'relative',
        // Cap to the app column + center, so on wider screens the panel matches
        // the centered phone-width layout instead of spanning the whole window.
        width: '100%', maxWidth: 480, alignSelf: 'center',
        background: 'var(--surface)',
        borderTopLeftRadius: 18, borderTopRightRadius: 18,
        boxShadow: '0 -8px 28px rgba(0,0,0,0.18)',
        maxHeight: '85%', height,
        display: 'flex', flexDirection: 'column',
        animation: 'kanjo-sheet-slide 0.22s ease-out',
      }}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 4px' }}>
          <div aria-hidden="true" style={{ width: 40, height: 4, borderRadius: 2, background: 'var(--ink-4)' }}/>
        </div>
        {title && (
          <div style={{ padding: '6px 20px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontSize: 16, fontWeight: 600 }}>{title}</div>
            <button onClick={onClose} aria-label="Close"
                    style={{ width: 32, height: 32, borderRadius: 16, background: 'var(--surface-3)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M3 3l8 8M11 3l-8 8"/></svg>
            </button>
          </div>
        )}
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 20px 24px' }}>{children}</div>
      </div>
      <style>{`
        @keyframes kanjo-sheet-fade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes kanjo-sheet-slide { from { transform: translateY(100%); } to { transform: translateY(0); } }
      `}</style>
    </div>
  );
}

// ─── GearButton ─────────────────────────────────────────────
// Small round cog button for a report/tab header's `right` slot. Opens the
// report's settings Sheet. Styled to match the help button in KHeader.
function GearButton({ onClick, label }) {
  const text = label || t('settings.title', 'Settings');
  return (
    <button onClick={onClick} aria-label={text} title={text}
      style={{ width: 30, height: 30, borderRadius: 15, background: 'var(--surface,#fff)',
        border: '1px solid var(--rule)', cursor: 'pointer',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        color: 'var(--ink-2)', padding: 0 }}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3"/>
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
      </svg>
    </button>
  );
}

// ─── Toast ──────────────────────────────────────────────────
// Singleton — fire via window.kanjoToast({ kind: 'success'|'error'|'info', text, action })
// Mounted once at the shell root via <ToastHost/>.
function ToastHost() {
  const [t, setT] = React.useState(null);
  React.useEffect(() => {
    let timer;
    window.kanjoToast = (next) => {
      if (timer) clearTimeout(timer);
      setT(next);
      const ttl = next?.ttl ?? 4000;
      if (ttl > 0) timer = setTimeout(() => setT(null), ttl);
    };
    return () => { if (timer) clearTimeout(timer); window.kanjoToast = undefined; };
  }, []);
  if (!t) return null;
  const palette = t.kind === 'error' ? { bg: 'var(--danger)', fg: '#fff' }
                : t.kind === 'success' ? { bg: 'var(--positive)', fg: '#fff' }
                : { bg: 'var(--ink)', fg: 'var(--surface)' };
  return (
    <div role="status" aria-live="polite" style={{
      position: 'absolute', left: 16, right: 16, bottom: 100, zIndex: 60,
      padding: '12px 16px', borderRadius: 12,
      background: palette.bg, color: palette.fg,
      display: 'flex', alignItems: 'center', gap: 12,
      boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
      animation: 'kanjo-toast-in 0.22s ease-out',
    }}>
      <div style={{ flex: 1, fontSize: 14, fontWeight: 500 }}>{t.text}</div>
      {t.action && (
        <button onClick={() => { t.action.onClick?.(); setT(null); }}
                style={{ background: 'transparent', border: 'none', color: 'inherit', fontWeight: 600, fontSize: 14, cursor: 'pointer', padding: '4px 6px' }}>
          {t.action.label}
        </button>
      )}
      <style>{`@keyframes kanjo-toast-in { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }`}</style>
    </div>
  );
}

// ─── Skeletons ──────────────────────────────────────────────
function Skeleton({ width = '100%', height = 12, radius = 6, style }) {
  return (
    <div aria-hidden="true" className="kanjo-skel"
         style={{ width, height, borderRadius: radius, background: 'var(--surface-3)', ...style }}/>
  );
}
function SkeletonText({ lines = 2, width = '100%' }) {
  return (
    <div>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} width={i === lines - 1 ? '60%' : width} height={10} style={{ marginBottom: 8 }}/>
      ))}
    </div>
  );
}
function SkeletonCard({ height = 120 }) {
  return <Skeleton width="100%" height={height} radius={16}/>;
}

// Pulse animation applies once globally.
(function injectSkeletonStyles() {
  if (typeof document === 'undefined') return;
  if (document.getElementById('kanjo-skel-style')) return;
  const s = document.createElement('style');
  s.id = 'kanjo-skel-style';
  s.textContent = `
    .kanjo-skel { position: relative; overflow: hidden; }
    .kanjo-skel::after {
      content: ""; position: absolute; inset: 0;
      background: linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent);
      animation: kanjo-skel-shimmer 1.5s ease-in-out infinite;
    }
    .kanjo-dark .kanjo-skel::after {
      background: linear-gradient(90deg, transparent, rgba(255,255,255,0.06), transparent);
    }
    @keyframes kanjo-skel-shimmer { from { transform: translateX(-100%); } to { transform: translateX(100%); } }
  `;
  document.head.appendChild(s);
})();

// ─── Stepper ────────────────────────────────────────────────
function Stepper({ value, onChange, min = 0, max = 999, label }) {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(String(value));
  React.useEffect(() => { setDraft(String(value)); }, [value]);

  const commit = () => {
    const n = Math.max(min, Math.min(max, Number(draft) || 0));
    onChange(n);
    setEditing(false);
  };
  const bump = (delta) => onChange(Math.max(min, Math.min(max, (value || 0) + delta)));

  return (
    <div role="group" aria-label={label}
         style={{ display: 'flex', alignItems: 'center', background: 'var(--surface-3)', borderRadius: 999, padding: 3 }}>
      <button onClick={() => bump(-1)} aria-label="Decrease"
              style={{ width: 44, height: 44, borderRadius: 999, background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 18 }}>−</button>
      {editing ? (
        <input autoFocus inputMode="numeric" value={draft}
               onChange={(e) => setDraft(e.target.value.replace(/[^0-9]/g, ''))}
               onBlur={commit}
               onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setDraft(String(value)); setEditing(false); } }}
               style={{ width: 56, height: 32, textAlign: 'center', borderRadius: 8, border: '1px solid var(--rule-2)', fontFamily: 'var(--mono)', fontSize: 14, background: 'var(--surface)', color: 'var(--ink)' }}/>
      ) : (
        <button onClick={() => setEditing(true)} aria-label="Edit value"
                className="mono" style={{ minWidth: 44, height: 32, padding: '0 8px', textAlign: 'center', fontSize: 14, fontWeight: 600, background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--ink)' }}>
          {value}
        </button>
      )}
      <button onClick={() => bump(+1)} aria-label="Increase"
              style={{ width: 44, height: 44, borderRadius: 999, background: 'var(--brand-fill)', color: 'var(--on-brand-fill)', border: 'none', cursor: 'pointer', fontSize: 18 }}>+</button>
    </div>
  );
}

// ─── KPI Tile / Row ────────────────────────────────────────
function KPITile({ label, value, sublabel, tone = 'neutral' }) {
  const toneColor = tone === 'positive' ? 'var(--positive)'
                  : tone === 'warning' ? 'var(--warning)'
                  : tone === 'danger' ? 'var(--danger)'
                  : 'var(--ink)';
  // Pre-formatted money strings get the small-grey-cents treatment so KPI
  // tiles share the Today hero style. JSX values render as-is.
  const inner = typeof value === 'string' && /^[^A-Za-z]*\$?-?\d/.test(value)
    ? <span className="figure" style={{ fontSize: 18, color: toneColor }}>{splitCents(value)}</span>
    : <span className="mono" style={{ fontSize: 18, fontWeight: 600, color: toneColor }}>{value}</span>;
  return (
    <div className="card" style={{ padding: 14 }}>
      <div className="label">{label}</div>
      <div style={{ marginTop: 6 }}>{inner}</div>
      {sublabel && <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 4 }}>{sublabel}</div>}
    </div>
  );
}

// Split a pre-formatted money string ("$1,234.56", "-$1,234.56", "1 234,56 €")
// into figure parts so existing `.figure` CSS shrinks/greys the cents — lets
// callers that pass a pre-formatted string match KMoney consumers' hierarchy.
function splitCents(s) {
  const m = /^(-?)([^\d-]*)(\d[\d, ]*)(?:[.,](\d+))?(.*)$/.exec(String(s));
  if (!m) return s;
  const [, neg, prefix, whole, frac, suffix] = m;
  return (
    <>
      {neg ? "−" : null}
      {prefix && <span className="currency">{prefix.trim()}</span>}
      {whole}
      {frac != null && <span className="cents">.{frac}</span>}
      {suffix}
    </>
  );
}
function KPIRow({ children }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(96px, 1fr))', gap: 10 }}>
      {children}
    </div>
  );
}

/**
 * A supplier's name, ready to put on screen.
 *
 * Bank and card merchants have gone through cashflow/util.js's cleanTxnName
 * since demo mode shipped; supplier names arrive by the other road — scanned
 * invoices into db.vendors — and had no equivalent, so demo mode printed real
 * suppliers on Price changes, Receipts, the order guide and everywhere else.
 * This is that missing chokepoint, in the one file every screen already loads.
 *
 * Display ONLY. Matching, dedup, price keys, invoice reassignment, exports and
 * backups all need the stored name — an export full of fictional suppliers is
 * worse than the leak this closes.
 *
 * @param {string} name the vendor's stored name
 * @param {string} [fallback] used when there is no vendor; identifies nobody, so
 *   it is never obscured
 */
function vendorName(name, fallback = 'Unknown vendor') {
  const label = window.kanjo?.demo?.vendorLabel;
  if (label) return label(name, fallback);
  return String(name == null ? '' : name).trim() || fallback;
}

Object.assign(window, { Empty, ErrorBanner, WarningBanner, Sheet, GearButton, ToastHost, Skeleton, SkeletonText, SkeletonCard, Stepper, KPITile, KPIRow, localToday, localMonth, vendorName });
